import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Service } from '@/types/dashboard';
import { ServiceCardContent } from './card/ServiceCardContent';
import { ServiceDetailDialog } from './dialogs/ServiceDetailDialog';
import { ServiceImageSection } from './image-handling/ServiceImageSection';
import ServiceCheckoutDialog from '@/components/service-checkout/ServiceCheckoutDialog';

interface ServiceImage {
  id: string;
  service_id: string;
  image_url: string;
  created_at: string;
}

interface ServiceCardProps {
  service: Service;
  serviceImages: ServiceImage[];
  selectedImageIndex: number;
  onImageNavigation: (direction: 'prev' | 'next') => void;
  onServiceSelect: () => void;
  currency?: string | null;
  businessName?: string;
}

export const ServiceCard = ({
  service,
  serviceImages,
  selectedImageIndex,
  onImageNavigation,
  onServiceSelect,
  currency,
  businessName,
}: ServiceCardProps) => {
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isServiceCheckoutOpen, setIsServiceCheckoutOpen] = useState(false);
  const [loadedImages, setLoadedImages] = useState<{[key: string]: boolean}>({});
  const [carouselApi, setCarouselApi] = useState<any>(null);
  const [current, setCurrent] = useState(0);
  
  // Check if service has images
  const hasImages = (serviceImages && serviceImages.length > 0 && serviceImages.some(img => img.image_url && img.image_url.trim() !== '')) || 
                   (service.image_url && service.image_url.trim() !== '');
  const hasMultipleImages = serviceImages && serviceImages.length > 1;
  
  // Debug log for serviceImages
  console.log(`ServiceCard for ${service.name}:`, {
    serviceImagesCount: serviceImages?.length,
    serviceImagesData: serviceImages,
    hasImages,
    hasMultipleImages,
    selectedImageIndex
  });
  
  // Initialize loadedImages state
  useEffect(() => {
    if (serviceImages && serviceImages.length > 0) {
      const initialLoadState: {[key: string]: boolean} = {};
      serviceImages.forEach(img => {
        const imageId = img.id || `${img.image_url || `image-${serviceImages.indexOf(img)}`}-${serviceImages.indexOf(img)}`;
        initialLoadState[imageId] = false;
      });
      console.log(`Initial loaded images state for ${service.name}:`, initialLoadState);
      setLoadedImages(initialLoadState);
    } else if (service.image_url && service.image_url.trim() !== '') {
      // Handle single image case - only if image URL is valid
      setLoadedImages({ [service.image_url]: false });
    }
  }, [serviceImages, service.image_url, service.name]);

  // Handle carousel API
  useEffect(() => {
    if (carouselApi && hasImages) {
      console.log(`Carousel API set for ${service.name}:`, {
        api: !!carouselApi,
        imageCount: serviceImages?.length,
        selectedImageIndex
      });
      
      carouselApi.scrollTo(selectedImageIndex);
      setCurrent(selectedImageIndex);
      
      const handleSelect = () => {
        if (!carouselApi) return;
        
        const newIndex = carouselApi.selectedScrollSnap();
        const previousIndex = current;
        
        console.log(`Carousel select event for ${service.name}:`, {
          previousIndex,
          newIndex,
          totalImages: serviceImages?.length
        });
        
        // Handle looping edge cases properly
        let direction: 'prev' | 'next';
        if (previousIndex === serviceImages.length - 1 && newIndex === 0) {
          direction = 'next'; // Looped from last to first
        } else if (previousIndex === 0 && newIndex === serviceImages.length - 1) {
          direction = 'prev'; // Looped from first to last
        } else {
          direction = newIndex < previousIndex ? 'prev' : 'next';
        }
        
        setCurrent(newIndex);
        onImageNavigation(direction);
      };
      
      carouselApi.on("select", handleSelect);
      
      return () => {
        carouselApi.off("select", handleSelect);
      };
    }
  }, [carouselApi, selectedImageIndex, current, onImageNavigation, hasImages, service.name, serviceImages?.length]);

  const handleRequestService = () => {
    setIsServiceCheckoutOpen(true);
  };
  
  const handleImageLoad = (imageId: string) => {
    console.log('Image loaded:', imageId);
    setLoadedImages(prev => ({
      ...prev,
      [imageId]: true
    }));
  };

  return (
    <>
      <Card className={`overflow-hidden hover:shadow-md transition-shadow flex flex-col group ${!hasImages ? 'compact-card' : 'h-full'}`}>
        {/* Only render image section if images exist */}
        {hasImages && (
          <div className="relative aspect-square overflow-hidden group">
            <ServiceImageSection
              serviceImages={serviceImages}
              serviceName={service.name}
              loadedImages={loadedImages}
              handleImageLoad={handleImageLoad}
              current={current}
              setCarouselApi={setCarouselApi}
              singleImageUrl={service.image_url || ''}
              hasMultipleImages={hasMultipleImages}
              context="customer"
            />
          </div>
        )}
        
        {/* Content Section */}
        <ServiceCardContent
          service={service}
          currency={currency}
          businessName={businessName}
          onViewClick={() => setIsViewDialogOpen(true)}
          onRequestClick={handleRequestService}
          hasImage={hasImages}
        />
      </Card>

      <ServiceDetailDialog
        service={service}
        serviceImages={serviceImages}
        open={isViewDialogOpen}
        onOpenChange={setIsViewDialogOpen}
        onRequestClick={() => {
          setIsViewDialogOpen(false);
          handleRequestService();
        }}
        loadedImages={loadedImages}
        handleImageLoad={handleImageLoad}
        current={current}
        setCarouselApi={setCarouselApi}
        businessName={businessName}
        currency={currency}
      />
      
      <ServiceCheckoutDialog
        isOpen={isServiceCheckoutOpen}
        onClose={() => setIsServiceCheckoutOpen(false)}
        service={{
          id: service.id,
          name: service.name,
          price: service.price,
          discounted_price: service.discounted_price,
          user_id: (service as any).user_id || '',
          serviceMode: service.service_mode,
          travelFee: service.travel_fee?.toString() || '0',
          currency: currency,
          service_area: service.service_area,
          requires_scheduling: service.requires_scheduling,
          duration: service.duration,
          duration_unit: service.duration_unit,
          instant_booking: service.instant_booking
        }}
        onSubmitSuccess={() => {
          setIsServiceCheckoutOpen(false);
        }}
        contextType="customer"
      />
    </>
  );
};
